/**
 * Comment Security - Client-side validation and protection
 * Simple security enhancements for comment forms
 */

(function() {
  'use strict';
  
  // ONLY allow this domain
  const ALLOWED_DOMAIN = 'hexahills.com';
  
  // Simple spam keyword detection (English only)
  const SPAM_KEYWORDS = [
    'viagra', 'casino', 'porn', 'bitcoin', 'crypto', 'loan', 'fuck', 'sex', 'suck', 'debt',
    'weight loss', 'make money', 'click here', 'free money', 'earn now',
    'buy now', 'limited time', 'act now', 'guaranteed', 'no risk', 'click below', 'subscribe now',
    'shit', 'damn', 'bitch', 'asshole', 'bastard', 'crap', 'piss', 'hell',
    'stupid', 'idiot', 'moron', 'retard', 'scam'
  ];
  
  /**
   * Check if text contains links (only hexahills.com allowed)
   */
  function containsLinks(text) {
    let hasExternalLink = false;
    
    // Check http/https URLs
    const httpMatches = text.match(/https?:\/\/([^\s<>"\/]+)/gi);
    if (httpMatches) {
      for (let match of httpMatches) {
        const hostMatch = match.match(/https?:\/\/([^\s<>"\/]+)/i);
        if (hostMatch && hostMatch[1]) {
          const foundHost = hostMatch[1].replace(/^www\./, '').toLowerCase();
          if (foundHost !== ALLOWED_DOMAIN) {
            return true; // Block - external domain
          }
        }
      }
      // If we have http/https links and all were hexahills.com, we're good
      return false;
    }
    
    // Check www. URLs
    const wwwMatches = text.match(/www\.([^\s<>"\/]+)/gi);
    if (wwwMatches) {
      for (let match of wwwMatches) {
        const foundHost = match.replace(/^www\./, '').toLowerCase().split(/[\/\s]/)[0];
        if (foundHost !== ALLOWED_DOMAIN) {
          return true; // Block - external domain
        }
      }
      return false;
    }
    
    // Check plain domain.tld patterns (only if no http/https found)
    const domainMatches = text.match(/([^\s@:\/]+)\.(com|org|net|edu|gov|mil|int|co|uk|de|fr|it|es|ru|cn|jp|au|ca|rs|hu|hr|si|ba|me|mk)\b/gi);
    if (domainMatches) {
      for (let match of domainMatches) {
        const foundHost = match.toLowerCase();
        if (foundHost !== ALLOWED_DOMAIN && foundHost !== 'www.' + ALLOWED_DOMAIN) {
          return true; // Block - external domain
        }
      }
      return false;
    }
    
    // Check BBcode [url]
    const bbcodeMatches = text.match(/\[url\s*=\s*['""]?([^\]'""]+)['""]?\]/gi);
    if (bbcodeMatches) {
      for (let match of bbcodeMatches) {
        const urlMatch = match.match(/\[url\s*=\s*['""]?([^\]'""]+)['""]?\]/i);
        if (urlMatch && urlMatch[1]) {
          const hostMatch = urlMatch[1].match(/https?:\/\/([^\s<>"\/]+)/i);
          if (hostMatch && hostMatch[1]) {
            const foundHost = hostMatch[1].replace(/^www\./, '').toLowerCase();
            if (foundHost !== ALLOWED_DOMAIN) {
              return true; // Block - external domain
            }
          }
        }
      }
      return false;
    }
    
    // Check HTML <a href>
    const hrefMatches = text.match(/<a\s+[^>]*href\s*=\s*['""]([^'""]+)['""]/gi);
    if (hrefMatches) {
      for (let match of hrefMatches) {
        const urlMatch = match.match(/href\s*=\s*['""]([^'""]+)['""]/i);
        if (urlMatch && urlMatch[1]) {
          const hostMatch = urlMatch[1].match(/https?:\/\/([^\s<>"\/]+)/i);
          if (hostMatch && hostMatch[1]) {
            const foundHost = hostMatch[1].replace(/^www\./, '').toLowerCase();
            if (foundHost !== ALLOWED_DOMAIN) {
              return true; // Block - external domain
            }
          }
        }
      }
      return false;
    }
    
    return false; // No links found
  }
  
  /**
   * Check if text contains spam keywords
   */
  function containsSpam(text) {
    const lowerText = text.toLowerCase();
    return SPAM_KEYWORDS.some(keyword => lowerText.includes(keyword));
  }
  
  /**
   * Check for suspicious patterns
   */
  function isSuspicious(text, msgAllCaps, msgPunctuation, msgRepeating) {
    // All caps (more than 60% uppercase)
    const uppercaseRatio = (text.match(/[A-Z]/g) || []).length / text.length;
    if (uppercaseRatio > 0.6 && text.length > 15) {
      return msgAllCaps || 'Too much text in uppercase';
    }
    
    // Excessive punctuation
    if (/[!?]{4,}/.test(text)) {
      return msgPunctuation || 'Excessive punctuation';
    }
    
    // Repeated characters
    if (/(.)\1{6,}/.test(text)) {
      return msgRepeating || 'Repeated characters detected';
    }
    
    return false;
  }
  
  /**
   * Validate comment content
   */
  function validateComment(content, textarea) {
    const trimmed = content.trim();
    
    // Get translated messages from data attributes
    const msgTooShort = textarea?.getAttribute('data-validation-too-short') || 'Comment is too short (minimum 3 characters)';
    const msgTooLong = textarea?.getAttribute('data-validation-too-long') || 'Comment is too long (maximum 2000 characters)';
    const msgExternalLinks = textarea?.getAttribute('data-validation-external-links') || 'External links are not allowed in comments';
    const msgSpam = textarea?.getAttribute('data-validation-spam') || 'Comment contains prohibited content';
    const msgAllCaps = textarea?.getAttribute('data-validation-all-caps') || 'Too much text in uppercase';
    const msgPunctuation = textarea?.getAttribute('data-validation-punctuation') || 'Excessive punctuation';
    const msgRepeating = textarea?.getAttribute('data-validation-repeating') || 'Repeated characters detected';
    
    // Length check
    if (trimmed.length < 3) {
      return msgTooShort;
    }
    
    if (trimmed.length > 2000) {
      return msgTooLong;
    }
    
    // Link check
    if (containsLinks(trimmed)) {
      return msgExternalLinks;
    }
    
    // Spam check
    if (containsSpam(trimmed)) {
      return msgSpam;
    }
    
    // Suspicious pattern check
    const suspiciousResult = isSuspicious(trimmed, msgAllCaps, msgPunctuation, msgRepeating);
    if (suspiciousResult) {
      return suspiciousResult;
    }
    
    return null; // Valid
  }
  
  /**
   * Show validation error
   */
  function showError(form, message) {
    // Remove existing error
    const existingError = form.querySelector('.DT-comment-error');
    if (existingError) {
      existingError.remove();
    }
    
    // Create error element
    const errorEl = document.createElement('div');
    errorEl.className = 'DT-comment-error bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mt-4';
    errorEl.textContent = message;
    
    // Insert after submit button
    const submitBtn = form.querySelector('input[type="submit"], button[type="submit"]');
    if (submitBtn) {
      submitBtn.parentNode.insertBefore(errorEl, submitBtn.nextSibling);
    } else {
      form.appendChild(errorEl);
    }
    
    // Scroll to error
    errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  
  /**
   * Remove validation error
   */
  function removeError(form) {
    const existingError = form.querySelector('.DT-comment-error');
    if (existingError) {
      existingError.remove();
    }
  }
  
  /**
   * Add character counter to textarea
   */
  function addCharacterCounter(textarea) {
    const maxLength = 2000;
    const wrapper = textarea.parentNode;
    // Avoid adding more than one counter per textarea wrapper
    if (wrapper.querySelector('.DT-char-counter')) {
      // Counter already exists for this textarea wrapper; ensure we don't add duplicate listeners either
      if (!textarea._pvCharCounterAttached) {
        // try to find existing counter and update it once
        const existing = wrapper.querySelector('.DT-char-counter');
        const updateExisting = function() {
          const length = textarea.value.length;
          const remaining = maxLength - length;
          existing.textContent = `${length}/${maxLength} karaktera`;
          if (remaining < 100) {
            existing.className = 'DT-char-counter text-sm text-orange-600 mt-1 text-right';
          } else if (remaining < 0) {
            existing.className = 'DT-char-counter text-sm text-red-600 mt-1 text-right font-semibold';
          } else {
            existing.className = 'DT-char-counter text-sm text-gray-500 mt-1 text-right';
          }
        };
        textarea.addEventListener('input', updateExisting);
        textarea._pvCharCounterAttached = true;
        updateExisting();
      }
      return;
    }

    // Create counter element
    const counter = document.createElement('div');
    counter.className = 'DT-char-counter text-sm text-gray-500 mt-1 text-right';
    
    function updateCounter() {
      const length = textarea.value.length;
      const remaining = maxLength - length;
  counter.textContent = `${length}/${maxLength} karaktera`;
      
      if (remaining < 100) {
        counter.className = 'DT-char-counter text-sm text-orange-600 mt-1 text-right';
      } else if (remaining < 0) {
        counter.className = 'DT-char-counter text-sm text-red-600 mt-1 text-right font-semibold';
      } else {
        counter.className = 'DT-char-counter text-sm text-gray-500 mt-1 text-right';
      }
    }
    
    // Insert counter
    wrapper.appendChild(counter);

    // Update on input - attach only once per textarea
    if (!textarea._pvCharCounterAttached) {
      textarea.addEventListener('input', updateCounter);
      textarea._pvCharCounterAttached = true;
    }
    updateCounter(); // Initial update
  }
  
  /**
   * Initialize comment security
   */
  function initCommentSecurity() {
    // Find comment forms
    const commentForms = document.querySelectorAll('#commentform, .comment-form, form[action*="wp-comments-post.php"]');
    
    commentForms.forEach(function(form) {
      const textarea = form.querySelector('textarea[name="comment"]');
      if (!textarea) return;
      
      // Add character counter
      addCharacterCounter(textarea);
      
      // Add real-time validation
      textarea.addEventListener('input', function() {
        removeError(form);
        
        const content = this.value;
        if (content.length > 0) {
          const error = validateComment(content, textarea);
          if (error && content.length > 10) { // Only show errors after some typing
            showError(form, error);
          }
        }
      });
      
      // Validate on form submit
      form.addEventListener('submit', function(e) {
        const content = textarea.value;
        const error = validateComment(content, textarea);
        
        if (error) {
          e.preventDefault();
          showError(form, error);
          textarea.focus();
          return false;
        }
        
        removeError(form);
        
        // Add simple timing protection
        const now = Math.floor(Date.now() / 1000);
        let timestampField = form.querySelector('input[name="dt_comment_timestamp"]');
        
        if (!timestampField) {
          timestampField = document.createElement('input');
          timestampField.type = 'hidden';
          timestampField.name = 'dt_comment_timestamp';
          form.appendChild(timestampField);
        }
        
        timestampField.value = now;
        
        // Add simple honeypot if not exists
        let honeypotField = form.querySelector('input[name="dt_website"]');
        if (!honeypotField) {
          honeypotField = document.createElement('input');
          honeypotField.type = 'text';
          honeypotField.name = 'dt_website';
          honeypotField.value = '';
          honeypotField.style.display = 'none';
          honeypotField.style.visibility = 'hidden';
          honeypotField.setAttribute('aria-hidden', 'true');
          honeypotField.setAttribute('tabindex', '-1');
          honeypotField.setAttribute('autocomplete', 'off');
          form.appendChild(honeypotField);
        }
      });
    });
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCommentSecurity);
  } else {
    initCommentSecurity();
  }
  
  // Re-initialize if comment forms are dynamically loaded
  if (typeof window.initCommentFormToggle === 'function') {
    const originalInit = window.initCommentFormToggle;
    window.initCommentFormToggle = function() {
      originalInit.apply(this, arguments);
      setTimeout(initCommentSecurity, 100);
    };
  }
  
})();
